import React, {useState} from 'react'
import {useIntl} from 'react-intl'
import ApplePayButton from 'apple-pay-button'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useShippingMethodsForShipment} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useBasket, useOrder, useAdyen, useCustomApi, useAnalytics} from '_customHooks'
import {useSFRAApplePayController} from '_sfraController'
import {OrderAddressModel, PaymentInstrumentModel, MiniCartPreferences, BasketModel} from '_types'
import {useLoader} from '_context'

interface ApplePayWrapperProps {
    customPrefs: MiniCartPreferences
    basketId?: string
    currency?: string
    subTotal?: number
    allOrderable?: boolean
    buttonLocation: 'minicart' | 'pdp'
    /**
     * Overrides the `accessType` sent to analytics on fast-checkout start. Defaults to `buttonLocation`.
     * Used to report a more specific provenance (e.g. 'confirm_layer_add_to_cart' from the mobile confirm layer)
     * without affecting the button-visibility logic driven by `buttonLocation`.
     */
    analyticsAccessType?: string
    onBeforeClick?: () => Promise<void>
    onAfterNotCompleted?: (basketItemId, newQuantity) => Promise<void>
    productId?: string
}

function ApplePayWrapper({
    customPrefs,
    basketId,
    currency,
    subTotal: _subTotal = 0,
    allOrderable,
    buttonLocation,
    analyticsAccessType,
    onBeforeClick,
    onAfterNotCompleted,
    productId,
}: ApplePayWrapperProps) {
    const {site, locale} = useMultiSite()
    const {
        updateBasketShippingAddress,
        updateBasketShippingMethod,
        updateBasketCustomer,
        addPaymentInstrumentToBasket,
        refetchBasket,
        removePaymentInstrument,
    } = useBasket(false)
    const {formatMessage} = useIntl()
    const {createOrder} = useOrder()
    const {showLoader} = useLoader()
    const {makeApplePayPayment} = useAdyen()
    const {analyticsExpressPaymentStart} = useAnalytics()
    const {data: customer} = useCurrentCustomer()
    const {useCalculateTaxesAvalara, useFailOrder} = useCustomApi()
    const {mutateAsync: calculateTaxesMutation} = useCalculateTaxesAvalara()
    const {mutateAsync: failOrderMutation} = useFailOrder()

    const [localBasketId] = useState(basketId)
    let subTotal = _subTotal
    let total = subTotal
    let totalTaxes = 0

    const [isCompletingOrder, setIsCompletingOrder] = useState(false)

    const {data: shippingMethods} = useShippingMethodsForShipment({
        parameters: {
            basketId: localBasketId || '',
            shipmentId: 'me',
        },
    })

    const isDisabled = buttonLocation === 'minicart' ? !subTotal || !allOrderable : false

    const {ApplePay_Enabled: isApplePayEnabled, hostLink, Allowed_Suretax_Dispatch: shouldCalculateTaxes} = customPrefs
    const hostLinkLocalized = JSON.parse(hostLink ?? '{}')[site.id]

    const useApplePayController = useSFRAApplePayController(hostLinkLocalized)

    const currentCountryCode = locale.id.split('-')[1].toUpperCase()
    const applePayConfig = {
        merchantId: customPrefs.ApplePay_MerchantId || [''],
        merchantName: customPrefs.ApplePay_MerchantName || '',
        merchantCapabilities: customPrefs.ApplePay_MerchantCapabilities || [],
        supportedNetworks: customPrefs.ApplePay_SupportedNetworks || [],
        requiredShippingContactFields: ['postalAddress', 'name', 'email', 'phone'] as ApplePayJS.ApplePayContactField[],
        requiredBillingContactFields: ['postalAddress', 'name'] as ApplePayJS.ApplePayContactField[],
    }

    const getLineItems = (): ApplePayJS.ApplePayLineItem[] => {
        return [
            {
                amount: total?.toString() ?? '0',
                label: formatMessage({id: 'label.subtotal', defaultMessage: 'SUBTOTAL'}),
                type: 'final' as ApplePayJS.ApplePayLineItemType,
            },
            {
                amount: '0',
                label: formatMessage({id: 'label.order.shipping.cost', defaultMessage: 'Shipment'}),
                type: 'final' as ApplePayJS.ApplePayLineItemType,
            },
            {
                amount: totalTaxes?.toString(),
                label: formatMessage({id: 'label.order.taxes', defaultMessage: 'TAXES'}),
                type: 'final' as ApplePayJS.ApplePayLineItemType,
            },
        ]
    }

    const getRequest = () => {
        const shippingMethod = shippingMethods?.applicableShippingMethods?.find((sm) => sm.id === shippingMethods?.defaultShippingMethodId)

        const request: ApplePayJS.ApplePayPaymentRequest = {
            countryCode: currentCountryCode,
            currencyCode: currency || locale.preferredCurrency,
            merchantCapabilities: applePayConfig.merchantCapabilities,
            supportedNetworks: applePayConfig.supportedNetworks,
            requiredShippingContactFields: applePayConfig.requiredShippingContactFields,
            requiredBillingContactFields: applePayConfig.requiredBillingContactFields,
            total: {
                label: applePayConfig.merchantName,
                type: 'final',
                amount: total?.toString() ?? '0',
            },
            shippingMethods: shippingMethod
                ? [
                      {
                          amount: shippingMethod.price?.toString() || '0',
                          identifier: shippingMethod.id,
                          detail: shippingMethod.description || '',
                          label: shippingMethod.name || '',
                      },
                  ]
                : [],
            lineItems: getLineItems(),
        }

        return request
    }

    const parseAppleAddress = (appleAddress: ApplePayJS.ApplePayPaymentContact): OrderAddressModel => {
        return {
            firstName: appleAddress.givenName,
            lastName: appleAddress.familyName,
            address1: appleAddress.addressLines?.[0],
            address2: appleAddress.addressLines?.[1],
            postalCode: appleAddress.postalCode,
            countryCode: appleAddress.countryCode,
            city: appleAddress.locality,
            stateCode: appleAddress.administrativeArea,
            ...(appleAddress.phoneNumber && {phone: appleAddress.phoneNumber}),
        }
    }

    const onClick = () => {
        // ApplePaySession MUST be created synchronously inside the user gesture handler, otherwise
        // Safari throws "InvalidAccessError: Must create a new ApplePaySession from a user gesture handler".
        // So we do NOT await onBeforeClick() here. The PDP add-to-cart + basket refetch run later, inside
        // onvalidatemerchant (the first async ApplePay event), before totals are recomputed and the order
        // is created. On minicart onBeforeClick is absent and this is a no-op.
        const applePayRequest = getRequest()
        /* eslint-disable @typescript-eslint/no-explicit-any */
        if (typeof window !== 'undefined' && window.ApplePaySession && typeof (window.ApplePaySession as any).prototype.begin === 'function') {
            const ApplePaySessionConstructor = window.ApplePaySession as unknown as {
                new (version: number, request: ApplePayJS.ApplePayPaymentRequest): ApplePaySession
            }
            const session = new ApplePaySessionConstructor(1, applePayRequest)
            handleEventsForApplePay(session)
            // Fast checkout start (manuale BitBang PDP PWA §3.1.6.1). accessType defaults to buttonLocation
            // unless a more specific provenance is provided (e.g. the mobile confirm layer).
            analyticsExpressPaymentStart({accessType: analyticsAccessType ?? buttonLocation, paymentType: 'ApplePay'})
            session.begin()
        }
    }

    const handleNotCompleted = async () => {
        if (onAfterNotCompleted) {
            try {
                const refetchResult = await refetchBasket()
                if (refetchResult.isSuccess) {
                    const updatedBasket = refetchResult.data?.baskets?.[0]
                    const basketItem = updatedBasket?.productItems?.find((item) => item.productId === productId)
                    const basketItemId = basketItem?.itemId
                    const newQuantity = (basketItem?.quantity || 1) - 1
                    await onAfterNotCompleted(basketItemId, newQuantity)
                }
            } catch {
                return false
            }
        }
    }

    function handleEventsForApplePay(session: ApplePaySession) {
        session.onvalidatemerchant = async (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
            // Call your own server to request a new merchant session.

            try {
                // Runs the PDP pre-checkout work here (not in onClick) because the session must be
                // created synchronously in the gesture. This is the first async ApplePay event, so it is
                // safe to await here — and it runs before totals are recomputed (onshippingcontactselected)
                // and before the order is created (onpaymentauthorized).
                if (onBeforeClick) {
                    await onBeforeClick()

                    const refetchResult = await refetchBasket()
                    if (refetchResult.isSuccess) {
                        const updatedTotal = refetchResult.data?.baskets?.[0]?.c_subtotal?.value
                        subTotal = updatedTotal ?? subTotal
                        total = subTotal
                    }
                }

                const body = {
                    isTrusted: true,
                    validationURL: event.validationURL,
                    hostname: window.location.hostname,
                }

                const merchantResponse = await useApplePayController.mutateAsync({body})

                if (merchantResponse?.session) {
                    session.completeMerchantValidation(merchantResponse.session)
                } else {
                    session.abort()
                    void handleNotCompleted()
                }
            } catch (err) {
                console.error('ApplePay onvalidatemerchant: error:', err)
                session.abort()
                void handleNotCompleted()
            }
        }

        session.onshippingcontactselected = async (event: ApplePayJS.ApplePayShippingContactSelectedEvent) => {
            // reset total
            total = subTotal

            const response: ApplePayJS.ApplePayShippingContactUpdate = {
                newTotal: {
                    label: applePayConfig.merchantName,
                    amount: total?.toString() ?? '0',
                },
                newLineItems: getLineItems(),
            }

            const handleError = (response: ApplePayJS.ApplePayShippingContactUpdate, error: Error) => {
                console.error('ApplePay onshippingcontactselected failed:', error)

                session.completeShippingContactSelection(
                    ApplePaySession.STATUS_INVALID_SHIPPING_POSTAL_ADDRESS,
                    [],
                    response.newTotal,
                    response.newLineItems ?? [],
                )
            }

            const shippingAddress = parseAppleAddress(event.shippingContact)

            // check shipping country
            if (shippingAddress?.countryCode !== currentCountryCode) {
                handleError(
                    response,
                    new Error(`shipping address country: ${shippingAddress?.countryCode} is different from current country ${currentCountryCode}`),
                )
                return
            }

            // set shipping method if needed
            await updateBasketShippingMethod(shippingMethods?.defaultShippingMethodId || '').catch((error) => {
                handleError(response, error)
                return
            })

            if (shouldCalculateTaxes) {
                await updateBasketShippingAddress(shippingAddress, false)

                const taxesResponse = await calculateTaxesMutation({basketId: basketId || '', applyTaxesToBasket: true}).catch((error) => {
                    handleError(response, error)
                })

                // update taxes and totals
                totalTaxes = taxesResponse?.totalTax ?? 0
                total = subTotal + totalTaxes

                // update response
                response.newTotal.amount = total?.toString() ?? '0'
                response.newLineItems = getLineItems()
            }

            session.completeShippingContactSelection(ApplePaySession.STATUS_SUCCESS, [], response.newTotal, response.newLineItems ?? [])
        }

        session.onpaymentauthorized = async (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
            setIsCompletingOrder(true)

            const paymentData = event.payment
            let continueUrl: string
            let orderNo: string | undefined

            const failPayment = async () => {
                session.completePayment(ApplePaySession.STATUS_FAILURE)
                console.error('ApplePay onpaymentauthorized failed')

                // reopen basket if an order was created
                if (orderNo) {
                    await failOrderMutation({locale: locale.id, orderId: orderNo})
                    await refetchBasket()
                }
            }

            if (paymentData.token) {
                // Forward token to your gateway for processing payment and return result to apple pay session

                const shippingContact = event.payment.shippingContact as ApplePayJS.ApplePayPaymentContact
                const shippingAddress = parseAppleAddress(shippingContact)

                try {
                    // set shipping and billing address
                    const updatedBasket: BasketModel = await updateBasketShippingAddress(shippingAddress, true)
                    const existingPaymentInstruments = updatedBasket?.paymentInstruments || []

                    // rimuovo tutti i payment instruments esistenti
                    for (const instrument of existingPaymentInstruments) {
                        if (instrument.paymentInstrumentId) {
                            await removePaymentInstrument(instrument.paymentInstrumentId)
                        }
                    }

                    const paymentInstrument: PaymentInstrumentModel = {
                        paymentMethodId: 'DW_APPLE_PAY',
                        amount: total,
                        c_applepayTransactionId: paymentData.token.transactionIdentifier,
                        c_applepayCard: paymentData.token.paymentMethod?.network,
                        paymentCard: {}, // leave empty
                    }
                    await addPaymentInstrumentToBasket(paymentInstrument)

                    // set customer email in basket
                    const email = customer.isGuest ? shippingContact.emailAddress : customer.email
                    await updateBasketCustomer({email})

                    // create order
                    const order = await createOrder(localBasketId)
                    orderNo = order?.orderNo

                    // make adyen payment
                    continueUrl = order?.c_responseData?.continueUrl
                    const adyenPaymentResponse = await makeApplePayPayment({
                        order,
                        countryCode: currentCountryCode,
                        paymentData: paymentData.token.paymentData,
                    })

                    // Se la chiamata fallisce o la risposta non è valida, fallisci il pagamento
                    if (adyenPaymentResponse?.error) {
                        await failPayment()
                        return
                    }

                    // complete applepay session and redirect to TYP
                    session.completePayment(ApplePaySession.STATUS_SUCCESS)

                    // redirect to TYP
                    if (continueUrl) {
                        showLoader()
                        window.location.href = continueUrl
                    }
                } catch (err) {
                    console.error('ApplePay onpaymentauthorized: failed:', err)
                    await failPayment()
                }
            } else {
                await failPayment()
            }
        }

        session.oncancel = () => {
            void handleNotCompleted()
            console.error('Session Cancelled.')
        }
    }

    if (isCompletingOrder) {
        return null
    }

    if (typeof window !== 'undefined') {
        if (!window.ApplePaySession || !window.ApplePaySession.canMakePayments()) {
            return <></>
        }
    }

    return (
        isApplePayEnabled && (
            <ApplePayButton
                onClick={onClick}
                locale={locale.id}
                style={{
                    width: '100%',
                    height: '42px',
                    borderRadius: '3px',
                    padding: '0px',
                }}
                type="plain"
                buttonStyle="white-outline"
                disabled={isDisabled}
            />
        )
    )
}

export {ApplePayWrapper}
